// Making an app for the M5Stack Tab5 in the room (docs/make.md#the-tab5).
//
// The workspace of src/ui/make.ts with a Tab5 app in it: the conversation
// on the left, the app on the Tab5's landscape screen beside it, the versions
// the agent saved under it, and the package of what is on screen to download:
// the file the screen runs is the file the Tab5 installs. An agent works on the app in a container of its own, so
// a message starts a turn that runs on after the request: the view reads the
// conversation and the app again every moment while a turn runs.

import { getTab5Game, newTab5Game, OutOfCredits, stopTab5Turn, tab5Conversation, tab5Message } from "../api/make.ts";
import { Refused, type StudioCore } from "../core/studio.ts";
import { turnPrice, type CreditsInfo, type FrameMessage, type Tab5GameInfo, type Tab5Message, type Tab5Part } from "../core/make.ts";
import { TAB5_SCREEN } from "../../maker/tab5/src/contract.ts";
import { h, svg } from "./dom.ts";
import { FACE, ICON, outOfCreditsMessage, prompter } from "./make-parts.ts";
import { t } from "./lang.ts";

/** A Tab5 app's id, as the room's focus names it beside a web game's `a...`. */
export const TAB5_GAME = /^t[0-9a-z]{12}$/;

/** How often the view reads the app again while a turn runs. */
const FOLLOW_MS = 1500;
/** The versions the tools row offers; older ones are kept by the maker but not shown. */
const VERSIONS_SHOWN = 6;

export interface Tab5Host {
  core(): StudioCore | null;
  toast(message: string): void;
  credits(): CreditsInfo | null;
  setCredits(credits: CreditsInfo): void;
}

/** What one tool call of the agent is, in words the visitor reads. */
function stepLabel(part: Extract<Tab5Part, { type: "dynamic-tool" }>): string {
  const input = (part.input ?? {}) as { path?: unknown; command?: unknown };
  const file = (typeof input.path === "string" ? input.path.split("/").pop() : "") || "a file";
  switch (part.toolName) {
    case "read":
      return t("make.tab5.step.read", { file });
    case "write":
    case "edit":
      return t("make.tab5.step.write", { file });
    case "grep":
    case "glob":
      return t("make.tab5.step.search");
    case "save_version":
      return t("make.tab5.step.save");
    case "bash": {
      const command = typeof input.command === "string" ? input.command : "";
      if (command.includes("tab5 shot")) return t("make.tab5.step.shot");
      if (command.includes("tab5 build")) return t("make.tab5.step.build");
      if (command.includes("site/content/docs")) return t("make.tab5.step.docs");
      return t("make.tab5.step.run");
    }
    default:
      return t("make.tab5.step.run");
  }
}

const textOf = (message: Tab5Message): string =>
  message.parts.flatMap((part) => (part.type === "text" && "text" in part ? [part.text] : [])).join("");

export class Tab5View {
  readonly space: HTMLElement;
  private heading: HTMLElement;
  private chat: HTMLElement;
  private field: HTMLTextAreaElement;
  private sendButton: HTMLButtonElement;
  private stopButton: HTMLButtonElement;
  private creditLine: HTMLElement;
  private screen: HTMLElement;
  private screenNote: HTMLElement;
  private tools: HTMLElement;
  private frame: HTMLIFrameElement | null = null;
  /** The app as the server last answered it. */
  private game: Tab5GameInfo | null = null;
  /** The id the view was opened for, before its app arrived; "" while a new app is being made. */
  private wanted: string | null = null;
  private talk: Tab5Message[] = [];
  /** A message this page sent that the conversation does not hold yet, and how many the visitor had sent before it. */
  private pending: { text: string; before: number } | null = null;
  /** The version on screen; null follows the newest. */
  private rev: number | null = null;
  /** An error the app's frame reported, for its author to have fixed. */
  private fault: string | null = null;
  private timer = 0;

  constructor(private host: Tab5Host) {
    this.heading = h("h2", { class: "make-title", text: t("make.tab5.new") });
    this.chat = h("ol", { class: "make-chat", "aria-label": t("make.chat.label"), "aria-live": "polite" });
    this.field = prompter(t("make.tab5.input.next"), () => this.fromComposer());
    this.sendButton = h("button", { class: "make-send", type: "button", onclick: () => this.fromComposer() }, h("span", { text: t("make.send") }), svg(ICON.send));
    this.stopButton = h("button", { class: "quiet", type: "button", onclick: () => void this.stop() }, t("make.tab5.stop"));
    this.creditLine = h("p", { class: "make-foot" });
    this.screenNote = h("div", { class: "make-screen-note" });
    this.screen = h("div", { class: "make-screen" }, this.screenNote);
    // The picture is the Tab5's: 640x360, shown at 2x across its landscape panel.
    const [width, height] = TAB5_SCREEN.auxiliary;
    this.screen.style.aspectRatio = `${width} / ${height}`;
    this.screen.style.setProperty("--aspect", String(width / height));
    this.tools = h("div", { class: "make-tools" });
    this.space = h("div", { class: "make-space tab5" },
      h("section", { class: "make-talk" }, this.heading, this.chat,
        h("div", { class: "make-compose" }, h("div", { class: "make-box" }, this.field, this.sendButton), h("div", { class: "make-foot-row" }, this.creditLine, this.stopButton))),
      h("section", { class: "make-stage" }, h("div", { class: "make-monitor tab5" }, this.screen), this.tools),
    );
    this.space.hidden = true;
    addEventListener("message", (event) => this.onFrame(event));
  }

  /** Shows the workspace, or hides it and lets go of the game. */
  show(on: boolean): void {
    if (on === !this.space.hidden) return;
    this.space.hidden = !on;
    if (!on) {
      this.reset();
    }
  }

  /** Opens one of the visitor's Tab5 apps. */
  open(id: string): void {
    if (id === (this.game?.id ?? this.wanted)) return;
    this.reset();
    this.wanted = id;
    this.render();
    void this.load(id);
  }

  /** A new app from its first message: the workspace shows the message at once, and the agent starts. */
  async begin(text: string): Promise<void> {
    this.reset();
    this.wanted = "";
    this.pending = { text, before: 0 };
    this.render();
    try {
      const turn = await newTab5Game(text);
      this.game = turn.game;
      this.wanted = turn.game.id;
      this.host.setCredits(turn.credits);
      this.host.core()?.openMake(turn.game.id);
      this.render();
      this.follow();
    } catch (error) {
      this.pending = null;
      this.render();
      this.refused(error, text);
    }
  }

  private async load(id: string): Promise<void> {
    try {
      const [game, talk] = await Promise.all([getTab5Game(id), tab5Conversation(id)]);
      if (this.wanted !== id) return;
      this.game = game;
      this.talk = talk.messages;
      this.render();
      if (game.busy) this.follow();
    } catch (error) {
      if (this.wanted !== id) return;
      this.host.toast(error instanceof Refused ? error.message : t("make.failed"));
      this.host.core()?.back();
    }
  }

  private reset(): void {
    clearTimeout(this.timer);
    this.game = null;
    this.wanted = null;
    this.talk = [];
    this.pending = null;
    this.rev = null;
    this.fault = null;
    for (const old of this.screen.querySelectorAll("iframe")) old.remove();
    this.frame = null;
  }

  /** Reads the conversation and the app again in a moment, and again after that while a turn runs. */
  private follow(): void {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(async () => {
      const id = this.game?.id;
      if (!id) return;
      const [talk, game] = await Promise.all([tab5Conversation(id).catch(() => null), getTab5Game(id).catch(() => null)]);
      if (this.game?.id !== id) return;
      if (talk) {
        this.talk = talk.messages;
        if (this.pending && this.talk.filter((message) => message.role === "user").length > this.pending.before) this.pending = null;
      }
      if (game) this.game = game;
      this.render();
      if (!game || game.busy) this.follow();
      else this.field.focus();
    }, FOLLOW_MS);
  }

  private get busy(): boolean {
    return !!this.pending || !!this.game?.busy;
  }

  private fromComposer(): void {
    const text = this.field.value.trim();
    if (!text || this.busy || !this.game) return;
    this.field.value = "";
    this.field.dispatchEvent(new Event("input"));
    void this.send(text);
  }

  private async send(text: string): Promise<void> {
    const game = this.game;
    if (!game) return;
    this.fault = null;
    this.pending = { text, before: this.talk.filter((message) => message.role === "user").length };
    this.render();
    try {
      const turn = await tab5Message(game.id, text);
      this.host.setCredits(turn.credits);
      if (this.game?.id === game.id) this.game = turn.game;
      this.render();
      this.follow();
    } catch (error) {
      this.pending = null;
      this.render();
      this.refused(error, text);
    }
  }

  /** A message the server would not take: the credits it lacked, or what it said; the words go back in the field. */
  private refused(error: unknown, text: string): void {
    if (error instanceof OutOfCredits) {
      this.host.setCredits(error.credits);
      this.chat.append(outOfCreditsMessage(error.price, error.credits));
      this.chat.scrollTop = this.chat.scrollHeight;
    } else this.host.toast(error instanceof Refused ? error.message : t("make.failed"));
    if (this.game) {
      this.field.value = text;
      this.field.dispatchEvent(new Event("input"));
    }
  }

  private async stop(): Promise<void> {
    const id = this.game?.id;
    if (!id) return;
    this.stopButton.disabled = true;
    await stopTab5Turn(id).catch((error) => this.host.toast(error instanceof Refused ? error.message : t("make.failed")));
    this.stopButton.disabled = false;
    this.follow();
  }

  // -------------------------------------------------------------- the views

  private render(): void {
    this.renderChat();
    this.renderScreen();
    this.renderComposer();
  }

  private maker(...content: Array<HTMLElement | null>): HTMLElement {
    return h("li", { class: "make-msg maker" }, h("span", { class: "make-face" }, svg(FACE)), h("div", {}, ...content));
  }

  private renderChat(): void {
    const items: HTMLElement[] = [];
    if (this.talk.length === 0 && !this.pending) items.push(this.maker(h("p", { text: t("make.tab5.hello") })));
    for (const message of this.talk) {
      if (message.settlement) {
        items.push(h("li", { class: "make-msg maker failed" }, h("span", { class: "make-face" }, svg(FACE)), h("div", {}, h("p", { text: t("make.tab5.stopped") }))));
        continue;
      }
      if (message.display !== "visible") continue;
      if (message.role === "user") {
        const text = textOf(message);
        if (text) items.push(h("li", { class: "make-msg you" }, h("p", { text })));
        continue;
      }
      if (message.role !== "assistant") continue;
      // A response is its words and its steps, in the order the agent took them.
      let words = "";
      let lastStep = "";
      const flush = () => {
        if (words.trim()) items.push(this.maker(h("p", { text: words.trim() })));
        words = "";
      };
      for (const part of message.parts) {
        if (part.type === "text" && "text" in part) {
          words += part.text;
          lastStep = "";
        } else if (part.type === "dynamic-tool" && "toolName" in part) {
          flush();
          const label = stepLabel(part);
          if (label !== lastStep) items.push(h("li", { class: "make-step", text: label }));
          lastStep = label;
        } else if (part.type === "data-version" && "data" in part) {
          flush();
          items.push(h("li", { class: "make-step saved", text: t("make.tab5.saved", { rev: (part.data as { rev: number }).rev }) }));
          lastStep = "";
        } else if (part.type === "data-draft") {
          flush();
          const label = t("make.tab5.draft.step");
          if (label !== lastStep) items.push(h("li", { class: "make-step", text: label }));
          lastStep = label;
        }
      }
      flush();
    }
    if (this.pending) items.push(h("li", { class: "make-msg you" }, h("p", { text: this.pending.text })));
    if (this.busy) items.push(h("li", { class: "make-msg maker live" }, h("span", { class: "make-face" }, svg(FACE)),
      h("p", { class: "make-dots", "aria-label": t("make.tab5.working") }, h("i"), h("i"), h("i"))));
    this.chat.replaceChildren(...items);
    this.chat.scrollTop = this.chat.scrollHeight;
    const newest = this.game?.versions.at(-1);
    this.heading.textContent = newest ? newest.title : t("make.tab5.new");
  }

  /**
   * What the screen plays: the version picked, or else the draft the agent
   * is trying out while there is one, or else the newest version. `rev` is
   * null for the draft.
   */
  private onScreen(): { play: string; download: string; title: string; rev: number | null } | null {
    const game = this.game;
    if (!game) return null;
    const picked = this.rev === null ? undefined : game.versions.find((version) => version.rev === this.rev);
    const version = picked ?? (game.draft ? null : game.versions.at(-1));
    if (version) return { play: version.play, download: version.download, title: version.title, rev: version.rev };
    return game.draft ? { play: game.draft.play, download: game.draft.download, title: game.draft.title, rev: null } : null;
  }

  private renderScreen(): void {
    const version = this.onScreen();
    if (version && (!this.frame || this.frame.dataset.play !== version.play)) {
      // The version before stays on screen until the new one says it is ready (onFrame).
      const frame = document.createElement("iframe");
      frame.className = this.frame ? "make-frame booting" : "make-frame";
      frame.title = version.title;
      // The player is on the Tab5 maker's own origin, never the Studio's: it keeps that origin to load its runtime.
      frame.setAttribute("sandbox", "allow-scripts allow-same-origin");
      frame.dataset.play = version.play;
      frame.src = version.play;
      this.frame = frame;
      this.screen.prepend(frame);
      this.fault = null;
      window.setTimeout(() => this.reveal(frame), 20_000);
    }
    if (!version) for (const old of this.screen.querySelectorAll("iframe")) old.remove();
    this.screen.classList.toggle("working", this.busy && !version);
    this.screen.classList.toggle("empty", !version);
    const note: HTMLElement[] = [];
    if (!version) {
      note.push(h("span", { class: "make-face big" }, svg(FACE)),
        h("b", { text: t(this.wanted !== null && !this.game && !this.pending ? "make.loading" : this.busy ? "make.tab5.working" : "make.tab5.screen.empty") }));
    } else if (!this.screen.querySelector("iframe[data-ready]")) {
      note.push(h("b", { class: "make-starting", text: t("make.tab5.screen.starting") }));
    } else if (this.fault) {
      const fault = this.fault;
      note.push(h("div", { class: "make-fault", role: "alert" },
        h("p", { text: t("make.tab5.error", { message: fault }) }),
        this.busy ? null : h("button", { class: "btn", type: "button", onclick: () => {
          this.fault = null;
          void this.send(t("make.tab5.error", { message: fault }));
        } }, t("make.error.fix", { n: turnPrice(true) })),
        h("button", { class: "quiet start", type: "button", onclick: () => { this.fault = null; this.renderScreen(); } }, t("make.error.ignore"))));
    }
    this.screenNote.replaceChildren(...note);
    this.screenNote.hidden = note.length === 0;
    this.renderTools();
  }

  private renderTools(): void {
    const version = this.onScreen();
    const versions = this.game?.versions ?? [];
    const draft = this.game?.draft ?? null;
    const tool = (icon: string, label: string, onclick: () => void, on = false) =>
      h("button", { class: "make-tool" + (on ? " on" : ""), type: "button", onclick }, svg(icon), h("span", { text: label }));
    if (!version) {
      this.tools.replaceChildren();
      return;
    }
    const items: Array<HTMLElement | null> = [
      tool(ICON.restart, t("make.restart"), () => { if (this.frame) this.frame.src = version.play; this.frame?.focus(); }),
      tool(ICON.full, t("make.fullscreen"), () => void this.frame?.requestFullscreen?.().catch(() => null)),
      ...(versions.length + (draft ? 1 : 0) > 1
        ? [h("span", { class: "make-versions", role: "group" },
          ...versions.slice(-VERSIONS_SHOWN).map((one) => this.pick(String(one.rev), t("make.tab5.version", { rev: one.rev }), one.rev, one.rev === version.rev)),
          draft ? this.pick(t("make.tab5.draft.short"), t("make.tab5.draft"), null, version.rev === null) : null)]
        : []),
      version.rev === null ? h("span", { class: "make-note", text: t("make.tab5.draft") }) : null,
      h("span", { class: "make-tools-gap" }),
      this.downloadTool(version.download),
    ];
    this.tools.replaceChildren(...items.filter((item): item is HTMLElement => item !== null));
  }

  /** A button of the version row: a version by its number, or the draft (`rev` null). */
  private pick(label: string, title: string, rev: number | null, on: boolean): HTMLElement {
    return h("button", {
      class: "make-version" + (on ? " on" : ""), type: "button", "aria-pressed": String(on), title,
      onclick: () => {
        // Picking the newest with no draft is following the newest, as picking the draft is.
        this.rev = rev === null || (rev === this.game?.versions.at(-1)?.rev && !this.game?.draft) ? null : rev;
        this.renderScreen();
      },
    }, label);
  }

  private renderComposer(): void {
    const game = this.game;
    const hasVersion = (game?.versions.length ?? 0) > 0;
    const placeholder = t(hasVersion ? "make.tab5.input.next" : "make.tab5.input.first");
    if (this.field.placeholder !== placeholder) {
      this.field.placeholder = placeholder;
      this.field.setAttribute("aria-label", placeholder);
      this.field.dispatchEvent(new Event("input"));
    }
    this.sendButton.disabled = this.busy || !game;
    this.sendButton.querySelector("span")!.textContent = t("make.send.price", { n: turnPrice(hasVersion) });
    this.stopButton.hidden = !game?.busy;
    const credits = this.host.credits();
    this.creditLine.textContent = this.busy ? t("make.tab5.working") : credits ? t("make.credits", { n: credits.balance }) : "";
  }

  /** The package of what is on screen: one press downloads it. */
  private downloadTool(href: string): HTMLElement {
    const link = h("a", { class: "make-tool", href, title: t("make.tab5.device.lead") }, svg(ICON.tab5), h("span", { text: t("make.tab5.device") }));
    link.setAttribute("download", "");
    return link;
  }

  // -------------------------------------------------------------- the frame

  private reveal(frame: HTMLIFrameElement): void {
    if (this.frame !== frame || frame.dataset.ready) return;
    frame.dataset.ready = "1";
    frame.classList.remove("booting");
    for (const old of this.screen.querySelectorAll("iframe")) if (old !== frame) old.remove();
    this.renderScreen();
  }

  private onFrame(event: MessageEvent): void {
    const frame = this.frame;
    if (!frame || event.source !== frame.contentWindow) return;
    const data = event.data as FrameMessage;
    if (!data || typeof data !== "object") return;
    if (data.pocket === "ready") this.reveal(frame);
    else if (data.pocket === "error" && !this.fault) {
      this.reveal(frame);
      this.fault = String(data.message).slice(0, 400);
      this.renderScreen();
    }
  }
}
